import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { InvoiceDto } from '@/api/generated';
import {
  useApproveInvoice,
  useClarifyInvoice,
  useDeleteInvoice,
  useDocumentBlobUrl,
  useDocuments,
  useInvoices,
  useObjectInvoice,
} from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatCurrencyEUR, formatRelativeDeadline, formatShortDate, toNumber } from '@/lib/format';
import styles from './InvoiceDetail.module.css';
import { InvoiceEditDialog } from './InvoiceDialogs';

// 2026-05-17 — the static WORKFLOW + LINES arrays were Phase-1 design
// handoff fixtures (Architect Jan V approved, hardcoded line items
// referencing predallen + welfsels). They rendered on every invoice
// regardless of the real status, which was misleading on a fresh project.
// Both removed. The workflow is now derived from the invoice's actual
// `status` field; line items aren't modelled on the API so the entire
// "Lijnposten" card is gone for v1.
type WorkflowStatus = 'done' | 'doing' | 'idle';

function buildWorkflow(status: string): Array<{ label: string; status: WorkflowStatus }> {
  // Reflect the invoice's lifecycle as four signal-states: Ontvangen,
  // Bouwheer-goedkeuring, Betaald, plus an Onbevestigd lane when status
  // is Overdue / clarification-requested.
  switch (status) {
    case 'Open':
      return [
        { label: 'Ontvangen', status: 'done' },
        { label: 'Bouwheer goedkeuren', status: 'doing' },
        { label: 'Betaald', status: 'idle' },
      ];
    case 'Paid':
      return [
        { label: 'Ontvangen', status: 'done' },
        { label: 'Bouwheer goedkeuren', status: 'done' },
        { label: 'Betaald', status: 'done' },
      ];
    case 'Overdue':
      return [
        { label: 'Ontvangen', status: 'done' },
        { label: 'Bezwaar lopende', status: 'doing' },
        { label: 'Betaald', status: 'idle' },
      ];
    default:
      return [{ label: status, status: 'doing' }];
  }
}

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const invoices = useInvoices({ pageSize: 50 });

  return (
    <AppFrame>
      <QueryBoundary query={invoices} loading="Factuur laden…">
        {(page) => {
          // BF-13 — remove the silent fall-through to page.items[0] that
          // previously masked broken links (the old /budget/factuur/${i+1}
          // index-based links). A missing id is now a real 404.
          const invoice = page.items.find((i) => i.id === id);
          if (!invoice) {
            return (
              <div role="alert" className="mono c-bad">
                Factuur niet gevonden.{' '}
                <Link to="/budget" className={styles.crumbLink}>
                  Terug naar budget
                </Link>
              </div>
            );
          }
          return <InvoiceBody invoice={invoice} />;
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function InvoiceBody({ invoice }: { invoice: InvoiceDto }) {
  const amount = toNumber(invoice.amount);
  const due = formatRelativeDeadline(invoice.dueAt);
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmClarify, setConfirmClarify] = useState(false);
  const [clarifyNote, setClarifyNote] = useState('');
  const [confirmObject, setConfirmObject] = useState(false);
  const [objectReason, setObjectReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const approve = useApproveInvoice();
  const clarify = useClarifyInvoice();
  const object = useObjectInvoice();
  const deleteMutation = useDeleteInvoice();
  const busy =
    approve.isPending || clarify.isPending || object.isPending || deleteMutation.isPending;
  const alreadyPaid = invoice.status === 'Paid';

  return (
    <>
      <div className={`row gap-12 ${styles.crumb}`}>
        <Btn variant="ghost" onClick={() => navigate('/budget')} aria-label="Terug naar factuuroverzicht">
          <Icon name="chevL" size={13} /> Terug naar overzicht
        </Btn>
        <span className={styles.crumbDivider} aria-hidden="true">·</span>
        <Link to="/budget" className={styles.crumbLink}>
          Budget
        </Link>
        <Icon name="chevR" size={11} />
        <span>Facturen</span>
        <Icon name="chevR" size={11} />
        <span className={styles.crumbCurrent}>{invoice.supplier}</span>
      </div>

      <div className={styles.body}>
        <div className="col gap-16">
          <Card className={styles.headerCard}>
            <div className={`row ${styles.headerRow}`}>
              <div>
                <div className={`row gap-8 ${styles.pillRow}`}>
                  {invoice.status === 'Overdue' ? (
                    <Pill kind="bad">{due}</Pill>
                  ) : invoice.status === 'Open' ? (
                    <Pill kind="warn">{due}</Pill>
                  ) : (
                    <Pill kind="ok">Betaald</Pill>
                  )}
                  <Pill kind="idle" dot={false}>
                    {invoice.status === 'Open' ? 'wacht op goedkeuring' : invoice.status}
                  </Pill>
                </div>
                <h1 className={styles.title}>Vorderingsstaat — {invoice.supplier}</h1>
                <div className={styles.refLine}>
                  {invoice.supplier} · referentie {invoice.id.slice(0, 8)}
                </div>
              </div>
              <div className={`ml-auto ${styles.amountBox}`}>
                <div className="eyebrow">Te betalen</div>
                <div className={styles.amount}>{formatCurrencyEUR(amount)}</div>
              </div>
            </div>

            <div className={styles.workflow}>
              <div className={`eyebrow ${styles.workflowLabel}`}>Goedkeuringsworkflow</div>
              <div className={`row gap-8 ${styles.workflowRow}`}>
                {buildWorkflow(invoice.status).map((s, i, arr) => (
                  <Fragment key={s.label}>
                    <div className={`col ${styles.step}`}>
                      <div className="row gap-6">
                        <div
                          className={`${styles.stepDot} ${
                            s.status === 'done'
                              ? styles.stepDotDone
                              : s.status === 'doing'
                                ? styles.stepDotDoing
                                : styles.stepDotIdle
                          }`}
                        >
                          {s.status === 'done' && (
                            <Icon name="check" size={11} color="white" strokeWidth={2.6} />
                          )}
                        </div>
                        <div
                          className={`${styles.stepLabel} ${s.status === 'doing' ? styles.stepLabelDoing : ''}`}
                        >
                          {s.label}
                        </div>
                      </div>
                    </div>
                    {i < arr.length - 1 && <div className={styles.stepLink} />}
                  </Fragment>
                ))}
              </div>
            </div>
          </Card>

          {/* "Lijnposten" card removed 2026-05-17 — the API has no
              invoice-line-item entity, so the previously-hardcoded "Beton
              verdiep 1 / Welfsels predallen / Metselwerk" rows were
              fixture data on every invoice. Re-introduce when a real
              relation lands. */}
          {invoice.documentId && <LinkedDocumentViewer documentId={invoice.documentId} />}
        </div>

        <div className="col gap-12">
          <Card className={styles.sideCard}>
            <h3>Acties</h3>
            <div className={`col gap-8 ${styles.actions}`}>
              <Btn
                variant="primary"
                className={styles.actionBtn}
                onClick={() => setConfirmApprove(true)}
                disabled={busy || alreadyPaid}
              >
                <Icon name="check" size={14} /> Goedkeuren &amp; betaling inplannen
              </Btn>
              <Btn
                className={styles.actionBtn}
                onClick={() => setConfirmClarify(true)}
                disabled={busy}
              >
                <Icon name="mail" size={14} /> Vraag toelichting
              </Btn>
              <Btn
                variant="ghost"
                className={`${styles.actionBtn} c-bad`}
                onClick={() => setConfirmObject(true)}
                disabled={busy}
              >
                <Icon name="flag" size={14} /> Bezwaar registreren
              </Btn>
              <Btn className={styles.actionBtn} onClick={() => setEditOpen(true)} disabled={busy}>
                <Icon name="edit" size={14} /> Bewerk
              </Btn>
              <Btn
                variant="ghost"
                className={`${styles.actionBtn} c-bad`}
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
              >
                <Icon name="trash" size={14} /> Verwijder
              </Btn>
            </div>
          </Card>
          <InvoiceEditDialog open={editOpen} onOpenChange={setEditOpen} invoice={invoice} />
          <ConfirmDialog
            open={confirmApprove}
            onOpenChange={setConfirmApprove}
            title="Factuur goedkeuren?"
            description="De factuur wordt op Betaald gezet en het budget wordt bijgewerkt."
            confirmLabel="Goedkeuren"
            pending={approve.isPending}
            onConfirm={async () => {
              try {
                await approve.mutateAsync(invoice.id);
                setConfirmApprove(false);
              } catch {
                // toast surfaced by the hook
              }
            }}
          />
          <ConfirmDialog
            open={confirmClarify}
            onOpenChange={(open) => {
              setConfirmClarify(open);
              if (!open) setClarifyNote('');
            }}
            title="Toelichting aanvragen?"
            description={
              <>
                <span className={styles.reasonIntro}>
                  Er wordt een mail naar {invoice.supplier} gestuurd met de vraag om
                  verduidelijking.
                </span>
                <label className={styles.reasonLabel}>
                  Toelichting (optioneel)
                  <textarea
                    className={styles.reasonInput}
                    value={clarifyNote}
                    onChange={(e) => setClarifyNote(e.target.value)}
                    placeholder="Wat moet de leverancier verduidelijken?"
                    rows={3}
                  />
                </label>
              </>
            }
            confirmLabel="Verstuur"
            pending={clarify.isPending}
            onConfirm={async () => {
              try {
                await clarify.mutateAsync({
                  id: invoice.id,
                  note: clarifyNote.trim() || null,
                });
                setConfirmClarify(false);
                setClarifyNote('');
              } catch {
                // toast surfaced by the hook
              }
            }}
          />
          <ConfirmDialog
            open={confirmObject}
            onOpenChange={(open) => {
              setConfirmObject(open);
              if (!open) setObjectReason('');
            }}
            title="Bezwaar registreren?"
            description={
              <>
                <span className={styles.reasonIntro}>
                  Het bezwaar wordt vastgelegd in het dossier. Goedkeuring is geblokkeerd tot het
                  bezwaar is afgehandeld.
                </span>
                <label className={styles.reasonLabel}>
                  Reden bezwaar (verplicht)
                  <textarea
                    className={styles.reasonInput}
                    value={objectReason}
                    onChange={(e) => setObjectReason(e.target.value)}
                    placeholder="Waarom is deze factuur niet correct?"
                    rows={3}
                    required
                  />
                </label>
              </>
            }
            confirmLabel="Registreer bezwaar"
            destructive
            pending={object.isPending || objectReason.trim().length === 0}
            onConfirm={async () => {
              if (objectReason.trim().length === 0) return;
              try {
                await object.mutateAsync({ id: invoice.id, reason: objectReason.trim() });
                setConfirmObject(false);
                setObjectReason('');
              } catch {
                // toast surfaced by the hook
              }
            }}
          />
          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title="Factuur verwijderen?"
            description="De factuur wordt definitief verwijderd uit het project."
            confirmLabel="Verwijder"
            destructive
            pending={deleteMutation.isPending}
            onConfirm={async () => {
              try {
                await deleteMutation.mutateAsync(invoice.id);
                setConfirmDelete(false);
                navigate('/budget');
              } catch {
                // toast surfaced by the hook
              }
            }}
          />

          <Card className={styles.sideCard}>
            <h3>Eigenschappen</h3>
            <div className={`col gap-8 ${styles.props}`}>
              <PropRow k="Vervaldatum" v={formatShortDate(invoice.dueAt)} />
              <PropRow k="Status" v={invoice.status} />
              <PropRow k="Leverancier" v={invoice.supplier} />
              {invoice.phaseId && <PropRow k="Gekoppelde fase" v={invoice.phaseId} />}
              <PropRow k="Bedrag" v={formatCurrencyEUR(amount)} />
              {invoice.documentId ? (
                <div className={`row gap-12 ${styles.propRow}`}>
                  <span className={styles.propKey}>Bestand</span>
                  <Link
                    to={`/documenten/${invoice.documentId}`}
                    className={`flex-1 ${styles.docLink}`}
                  >
                    <Icon name="paperclip" size={12} /> Open gekoppeld bestand
                  </Link>
                </div>
              ) : (
                <PropRow k="Bestand" v="— geen bestand gekoppeld" />
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function PropRow({ k, v }: { k: string; v: string }) {
  return (
    <div className={`row gap-12 ${styles.propRow}`}>
      <span className={styles.propKey}>{k}</span>
      <span className={`flex-1 ${styles.propValue}`}>{v}</span>
    </div>
  );
}

/**
 * Render the linked document inline so the user doesn't have to navigate to
 * /documenten/:id to see it. PDFs go in an iframe, images in an <img>; other
 * mime types fall back to a download/preview link. Falls back gracefully when
 * the blob is still loading or fetch fails.
 */
function LinkedDocumentViewer({ documentId }: { documentId: string }) {
  const docs = useDocuments({ pageSize: 100 });
  const doc = docs.data?.items.find((d) => d.id === documentId);
  const isPdf = doc?.mime === 'application/pdf';
  const isImage = doc?.mime.startsWith('image/');
  const canPreview = isPdf || isImage;
  const blobUrl = useDocumentBlobUrl(canPreview ? documentId : undefined);

  return (
    <Card className={styles.docCard}>
      <div className={`row ${styles.docHeader}`}>
        <h3 className={styles.docTitle}>Gekoppeld bestand</h3>
        {doc && (
          <Link to={`/documenten/${doc.id}`} className={`ml-auto ${styles.docLink}`}>
            <Icon name="chevR" size={11} /> open in documenten
          </Link>
        )}
      </div>
      {doc && (
        <div className={`mono ${styles.docMeta}`}>
          {doc.name} · {doc.mime}
        </div>
      )}
      <div className={styles.docViewer}>
        {!doc ? (
          <div className={`mono ${styles.docState}`}>Bestand laden…</div>
        ) : !canPreview ? (
          <div className={`col gap-8 ${styles.docState}`}>
            <Icon name="paperclip" size={20} color="var(--ink-3)" />
            <span>Geen inline preview voor {doc.mime}.</span>
          </div>
        ) : blobUrl.isPending ? (
          <div className={`mono ${styles.docState}`}>Preview laden…</div>
        ) : blobUrl.isError || !blobUrl.data ? (
          <div className={`mono ${styles.docState} c-bad`}>
            Preview niet beschikbaar — open in documenten om te downloaden.
          </div>
        ) : isPdf ? (
          <iframe src={blobUrl.data} title={doc.name} className={styles.docFrame} />
        ) : (
          <img src={blobUrl.data} alt={doc.name} className={styles.docImage} />
        )}
      </div>
    </Card>
  );
}
